import { Ban, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { LegacyGenerationJob } from "~/entities/chat";
import { formatHmsDuration } from "~/shared/hooks/useElapsedTimer";
import { cn } from "~/shared/lib/utils";

interface MessageCanceledProps {
    job: LegacyGenerationJob;
    onRetry?: () => void;
    isRetrying?: boolean;
}

export function MessageCanceled({ job, onRetry, isRetrying = false }: MessageCanceledProps) {
    const { t } = useTranslation("chat");
    const elapsed = job.durationSeconds != null ? formatHmsDuration(Math.round(job.durationSeconds)) : null;

    return (
        <div className="mt-3 flex items-center gap-3 rounded-lg border border-zinc-500/10 bg-zinc-500/[0.06] p-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-500/10">
                <Ban className="h-4 w-4 text-zinc-400" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-zinc-300">{t("messages.canceled.title")}</div>
                <div className="mt-0.5 text-xs text-zinc-500">
                    {elapsed != null
                        ? t("messages.canceled.descriptionWithElapsed", { elapsed })
                        : t("messages.canceled.description")}
                </div>
            </div>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    disabled={isRetrying}
                    className={cn(
                        "inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-xs font-medium",
                        "text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-200",
                        isRetrying && "cursor-not-allowed opacity-50",
                    )}
                >
                    <RefreshCw className={cn("h-3 w-3", isRetrying && "animate-spin")} />
                    {isRetrying ? t("messages.canceled.retrying") : t("messages.canceled.retry")}
                </button>
            )}
        </div>
    );
}
